import { GATE_SUMMARY_HINT } from './os-gate'
import {
    CONTACT_FIELDS,
    MARKDOWN_BODY,
    MARKDOWN_BODY_SENTENCE,
    mailBodyErrors,
    RECIPIENTS_SENTENCE,
    REPLY_RECIPIENT_FIELDS,
    REPLY_RECIPIENTS_SENTENCE,
    recipientErrors,
    recipientsField,
    replyRecipientErrors,
} from './os-my-mail'
import type { BuiltInTool, BuiltInToolError } from './types'

const MAILBOX =
    "The run's assigned member. The mailbox is always theirs: a `memberId` in the payload is ignored, and an OS-owned task has no mailbox."

const NO_MAILBOX: readonly BuiltInToolError[] = [
    { code: 'no_assigned_member', when: 'The run has no assigned member, so no mailbox.', handling: 'tool_error' },
    { code: 'member_not_found', when: "The run's member no longer exists.", handling: 'tool_error' },
]

/** The opt-out every send tool takes (#1115): the OS adds the mailbox's signature unless the call says not to. */
const SIGNATURE_FLAG = {
    type: 'boolean',
    description: 'false sends without the mailbox’s signature. Left out, the OS adds it after the body',
} as const

/** The reply's opt-out (#1116): the OS quotes the message being answered below the reply unless the call says not to. */
const QUOTE_FLAG = {
    type: 'boolean',
    description:
        'false sends without the original quoted below. Left out, the OS quotes the message being answered after the body and signature',
} as const

const NOT_IN_MAILBOX: BuiltInToolError = {
    code: 'email_not_found',
    when: "The id is not in this member's mailbox, including another member's message.",
    handling: 'tool_error',
}

/** The one state a stored mail is in (os#909); mutually exclusive, and only `snoozed` carries a date. */
const HANDLED_STATES = ['unhandled', 'replied', 'skipped', 'snoozed', 'escalated'] as const

const TRIAGE_FIELDS =
    '`to`, `cc`, `bcc` (who a mail the mailbox sent went to unseen, `[]` on everything else), `threadId`, `labels` (free text, `[]` when none), `handledState` (`unhandled`, `replied`, `skipped`, `snoozed` or `escalated`) and `snoozedUntil` (only when `snoozed`)'

/**
 * The assigned member's OS-hosted mailbox: `os.mail_list`/`os.mail_read` over what arrived, and
 * `os.mail_reply`/`os.mail_send` to answer from it (os#910). Both send tools carry `needs.gate: 'always'`,
 * so the CEO approves the exact outbound payload before anything leaves, whatever the task declares.
 * A reply the CEO asked for when answering an escalation is gated the same way (`os.escalation`).
 * What triage decides about a mail is recorded through `OS_MAIL_TRIAGE_TOOLS` below.
 */
export const OS_MAIL_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.mail_list',
        family: 'os.mail',
        summary: "List the assigned member's inbox, newest first: metadata and a snippet, no bodies.",
        description:
            'List mail in the inbox of the member doing this run, newest first. Metadata and a snippet only, no bodies: read one with os.mail_read. Optional unreadOnly (only messages not yet opened), label (only mail carrying that label), handledState (only mail in that state), threadId (every mail in one conversation), search (only mail whose sender address or name, subject or text contains the words, any case), order (desc, the default, or asc), page and limit (default 25, max 200). Filters combine: label VIP with handledState unhandled lists only unhandled VIP mail. A full row is large: about 30 fit in one result. To work through a long backlog pass compact true, which returns only id, fromAddress, subject, receivedAt, labels, handledState, spamScore and spamFlag, so about 100 rows fit.',
        inputSchema: {
            type: 'object',
            properties: {
                unreadOnly: { type: 'boolean', description: 'Only messages nobody has opened yet' },
                label: { type: 'string', description: 'Only mail carrying this label, e.g. VIP' },
                handledState: {
                    type: 'string',
                    enum: HANDLED_STATES,
                    description: 'Only mail in this handled state',
                },
                threadId: { type: 'string', description: 'Only mail in this conversation, from a row’s threadId' },
                search: {
                    type: 'string',
                    description: 'Only mail whose sender address or name, subject or text contains this, any case',
                },
                order: { type: 'string', enum: ['desc', 'asc'], description: 'By receivedAt. Default desc' },
                page: { type: 'integer', minimum: 1 },
                limit: { type: 'integer', minimum: 1, maximum: 200 },
                compact: {
                    type: 'boolean',
                    description:
                        'Only id, fromAddress, subject, receivedAt, labels, handledState, spamScore and spamFlag on each row',
                },
            },
            additionalProperties: false,
        },
        returns: `\`{ data, total, page, limit, pages }\`. Each row has \`id\`, \`fromAddress\`, \`fromName\`, \`subject\`, \`snippet\`, \`receivedAt\`, \`readAt\`, \`hasAttachments\`, \`threadKey\` (the same on every message in one conversation), \`direction\` (\`inbound\` when it arrived, \`outbound\` when the member sent it), ${TRIAGE_FIELDS}, \`spamScore\` and \`spamFlag\` (null if the receiving edge has not scored the message). With \`compact\`, each row has only \`id\`, \`fromAddress\`, \`subject\`, \`receivedAt\`, \`labels\`, \`handledState\`, \`spamScore\` and \`spamFlag\`.`,
        callers: ['run', 'api'],
        effect: 'read',
        retry: 'safe',
        needs: { member: MAILBOX },
        errors: [
            ...NO_MAILBOX,
            {
                code: 'handled_state_invalid',
                when: 'handledState is not one of the handled states.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'GET', path: '/v1/members/:id/emails', scope: 'members:read' },
        examples: [
            { input: { unreadOnly: true, limit: 10 } },
            { input: { handledState: 'unhandled', order: 'asc', compact: true, limit: 100 } },
        ],
        public: true,
    },
    {
        name: 'os.mail_read',
        family: 'os.mail',
        summary: "Read one message from the assigned member's inbox.",
        description:
            'Read one message from the assigned member’s inbox: sender, subject, text and sanitised HTML bodies, and attachment names (not their contents). Reading marks it read for the member too. Pass id from os.mail_list.',
        inputSchema: {
            type: 'object',
            properties: { id: { type: 'string', description: 'A message id from os.mail_list' } },
            required: ['id'],
            additionalProperties: false,
        },
        returns: `The message: sender, subject, \`textBody\`, sanitised \`htmlBody\`, \`receivedAt\`, \`readAt\`, \`attachments\` (names and sizes, not contents), ${TRIAGE_FIELDS}, \`spamScore\` and \`spamFlag\` (null if the receiving edge has not scored the message).`,
        callers: ['run', 'api'],
        effect: 'read',
        sideEffects: 'Marks the message read for the member, the same as opening it in the portal.',
        retry: 'safe',
        needs: { member: MAILBOX },
        errors: [...NO_MAILBOX, NOT_IN_MAILBOX],
        api: { method: 'GET', path: '/v1/members/:id/emails/:emailId', scope: 'members:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000003' } }],
        public: true,
    },
    {
        name: 'os.mail_reply',
        family: 'os.mail',
        summary: 'Reply to a stored mail using its mailbox reply identity, once the CEO approves.',
        description:
            'Reply to one message in the assigned member’s inbox. Pass id from os.mail_list and the reply. ' +
            MARKDOWN_BODY_SENTENCE +
            ' The subject is taken from the message being answered. ' +
            REPLY_RECIPIENTS_SENTENCE +
            ' Replies use the arrival address unless its alias is configured to reply from the member’s primary address. Both addresses must still belong to this mailbox. The reply threads under the original in the recipient’s mail client. When the mailbox has a signature, the OS adds it after the body, in text and html, before the approval card opens; pass signature false to send without it. The OS then quotes the message being answered below the signature, under an `On <date>, <name> wrote:` line, as `>` lines in text and a blockquote in html, also before the approval card opens; pass quote false to send without it. Every send waits for the CEO to approve this exact payload, signature, quote and every recipient included (a reply-all names everyone it copies in, in cc, before the approval card opens), and approval sends that and nothing else. The sent reply is filed in the member’s mailbox in the original’s thread, and the original is marked read and moves to handled state replied.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.mail_list: the mail this answers' },
                markdown: MARKDOWN_BODY,
                text: { type: 'string', description: 'The reply, as plain text, when not given as markdown' },
                html: { type: 'string', description: 'Optional HTML body beside text. Plain text is sent either way' },
                ...REPLY_RECIPIENT_FIELDS,
                signature: SIGNATURE_FLAG,
                quote: QUOTE_FLAG,
            },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            '`{ status, id }`: `sent`, or `send_failed` with the provider’s `error` when it did not go out. `id` is the copy filed in the mailbox either way.',
        callers: ['run'],
        effect: 'write',
        sideEffects:
            'Files the sent reply in the member’s mailbox, in the original’s thread, marks the original read and, once sent, moves it to handled state `replied` (clearing any snooze) and saves every recipient to the member’s contacts.',
        retry: 'unsafe',
        retryNote: 'A repeat sends a second reply. Call it again only if the first reported a refusal.',
        needs: {
            member: MAILBOX,
            gate: 'always',
        },
        errors: [
            ...NO_MAILBOX,
            NOT_IN_MAILBOX,
            ...mailBodyErrors('hand_back'),
            ...replyRecipientErrors('tool_error'),
            {
                code: 'sender_not_own_mailbox',
                when: "The address the original arrived at does not deliver to this member's mailbox.",
                handling: 'tool_error',
            },
            {
                code: 'gate_summary_required',
                when: 'The run has not written the recipient and gist the CEO should read on the approval card.',
                handling: 'hand_back',
                hint: GATE_SUMMARY_HINT,
            },
        ],
        examples: [
            { input: { id: '5f0c1d2e-0000-4000-8000-000000000003', markdown: 'Thursday at **10:00** works.' } },
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000003',
                    replyAll: true,
                    markdown: 'Thursday at **10:00** works for all of us.',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.mail_send',
        family: 'os.mail',
        summary: 'Send a new mail from the assigned member’s mailbox, once the CEO approves.',
        description:
            'Send a new message from the assigned member’s mailbox: to, subject and the body. ' +
            RECIPIENTS_SENTENCE +
            ' ' +
            MARKDOWN_BODY_SENTENCE +
            ' It goes out from the member’s own address unless from names another address that delivers to the same mailbox — any other sender is refused, whatever the payload says. When the mailbox has a signature, the OS adds it after the body, in text and html, before the approval card opens; pass signature false to send without it. Every send waits for the CEO to approve this exact payload, signature and every recipient included, and approval sends that and nothing else. The sent message is filed in the member’s mailbox. To answer a message already in the inbox, use os.mail_reply instead, so it threads.',
        inputSchema: {
            type: 'object',
            properties: {
                to: recipientsField('Who the mail is to: one email address, or a list'),
                cc: recipientsField('Who is copied in, seen by every recipient: one address or a list'),
                bcc: recipientsField('Who is sent the mail unseen by any other recipient: one address or a list'),
                subject: { type: 'string' },
                markdown: MARKDOWN_BODY,
                text: { type: 'string', description: 'The message, as plain text, when not given as markdown' },
                html: { type: 'string', description: 'Optional HTML body beside text. Plain text is sent either way' },
                from: {
                    type: 'string',
                    description: 'Another address that delivers to this member’s mailbox. Defaults to their own',
                },
                signature: SIGNATURE_FLAG,
            },
            required: ['to', 'subject'],
            additionalProperties: false,
        },
        returns:
            '`{ status, id }`: `sent`, or `send_failed` with the provider’s `error` when it did not go out. `id` is the copy filed in the mailbox either way.',
        callers: ['run'],
        effect: 'write',
        sideEffects:
            'Files the sent message in the member’s mailbox, every recipient on it, and, once sent, saves every recipient to its contacts.',
        retry: 'unsafe',
        retryNote: 'A repeat sends a second message. Call it again only if the first reported a refusal.',
        needs: {
            member: MAILBOX,
            gate: 'always',
        },
        errors: [
            ...NO_MAILBOX,
            ...mailBodyErrors('hand_back'),
            ...recipientErrors('hand_back'),
            {
                code: 'sender_not_own_mailbox',
                when: "`from` names an address that does not deliver to this member's mailbox. Leave `from` out to send from the member's own address.",
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    to: 'vip@acme.com',
                    subject: 'Thursday',
                    markdown: 'Thursday at **10:00** works for the CEO.',
                },
            },
            {
                input: {
                    to: ['vip@acme.com', 'ops@acme.com'],
                    cc: 'assistant@acme.com',
                    subject: 'Thursday',
                    markdown: 'Thursday at **10:00** works for the CEO.',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.mail_contacts',
        family: 'os.mail',
        summary:
            "List or search the assigned member's contacts: the people its mailbox has written to, and those added.",
        description:
            'List the contacts of the assigned member’s mailbox: everyone a send or reply from it went to, saved once it left, and everyone added by hand. Most recently written to first, then by name. Pass search to keep only contacts whose name or address contains it, any case: to mail Sarah from Acme, search Sarah or acme and use the address it returns in os.mail_send. Pass directory true to add the enterprise’s own people and members who have an address, after the contacts and each only once. limit is how many rows (default 50, max 200). Mail that arrived never makes a contact.',
        inputSchema: {
            type: 'object',
            properties: {
                search: {
                    type: 'string',
                    description: 'Only contacts whose name or address contains this, any case',
                },
                directory: {
                    type: 'boolean',
                    description: 'Also offer the enterprise’s own people and members who have an address',
                },
                limit: { type: 'integer', minimum: 1, maximum: 200, description: 'Rows to return. Default 50' },
            },
            additionalProperties: false,
        },
        returns: `\`{ data }\`: each contact with ${CONTACT_FIELDS}.`,
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        needs: { member: MAILBOX },
        errors: [
            ...NO_MAILBOX,
            {
                code: 'contact_search_invalid',
                when: 'search is not text, or is longer than 200 characters.',
                handling: 'tool_error',
            },
            {
                code: 'contact_limit_invalid',
                when: 'limit is not a whole number from 1 to 200.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { search: 'sarah' } }, { input: { search: 'acme', directory: true, limit: 10 } }],
        public: true,
    },
    {
        name: 'os.mail_contact_save',
        family: 'os.mail',
        summary: "Add a contact to the assigned member's mailbox, or name one already there.",
        description:
            'Save an address to the contacts of the assigned member’s mailbox. Pass address and, optionally, name. A new address is added as one saved by hand; an address already among the contacts keeps its history and takes the name when one is given. A name set here is kept: a later send never replaces it. Leave name out to keep the name a contact already has. It only records the contact, and sends nothing.',
        inputSchema: {
            type: 'object',
            properties: {
                address: { type: 'string', description: 'The contact’s email address' },
                name: {
                    type: 'string',
                    description: 'The name the contact goes by. Kept over any a later send carries',
                },
            },
            required: ['address'],
            additionalProperties: false,
        },
        returns: `The contact saved, with ${CONTACT_FIELDS}.`,
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        needs: { member: MAILBOX },
        errors: [
            ...NO_MAILBOX,
            {
                code: 'contact_address_invalid',
                when: 'address is not one email address.',
                handling: 'tool_error',
            },
            {
                code: 'contact_name_invalid',
                when: 'name is not text, or is longer than 200 characters.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { address: 'sarah@acme.com', name: 'Sarah Jones' } }],
        public: true,
    },
    {
        name: 'os.member_mailbox_actions',
        family: 'os.mail',
        summary:
            "List the one-click buttons a member's mailbox offers: the tasks that declare a mailbox action and run as a role the member holds.",
        description:
            'List the one-click buttons a member’s mailbox offers, as the member’s page shows them: each an active task of the enterprise whose current version declares a mailbox action (mailboxAction) and that runs as a role this member holds, since those are the runs that read the member’s mailbox. on says where the button sits: mailbox, over the whole mailbox, or mail, on one message. Start one with os.start_task by its taskId; for a button on one message pass the message’s id as the emailId input. The run reads the member’s mailbox as its tasks always have. Pass id, the member’s id. A member in another enterprise, or one holding no such role, offers nothing.',
        inputSchema: {
            type: 'object',
            properties: { id: { type: 'string', description: 'The member’s id' } },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            '`{ actions }`: each `{ taskId, catalogTaskId, slug, name, label, on }`, `taskId` always set here, by label.',
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/members/:id/mailbox-actions', scope: 'members:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000031' } }],
        public: true,
    },
]

/**
 * What triage records about a mail in the assigned member's mailbox (os#909): its labels and its handled state.
 * A family of their own, beside `os.mail` as `os.escalation` is: `os.mail` reads and sends the mail itself,
 * these only record a decision about it. Ungated, because they change nothing outside the member's own
 * mailbox, and scoped to it exactly as `os.mail` is — the same `MailTools` answers both.
 */
export const OS_MAIL_TRIAGE_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.mail_set_label',
        family: 'os.mail_triage',
        summary: 'Set the labels on a mail in the assigned member’s inbox.',
        description:
            'Set the labels triage gives one message in the assigned member’s inbox. Pass id from os.mail_list and labels, the whole set the mail should carry: it replaces whatever labels it had, and an empty list clears them. Labels are free text (e.g. VIP, Needs reply); there is no fixed list to choose from. List mail by label with os.mail_list.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.mail_list' },
                labels: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Every label the mail should carry. Replaces the current set',
                },
            },
            required: ['id', 'labels'],
            additionalProperties: false,
        },
        returns: '`{ id, labels }`: the mail and the labels it now carries.',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        needs: { member: MAILBOX },
        errors: [
            ...NO_MAILBOX,
            NOT_IN_MAILBOX,
            {
                code: 'labels_invalid',
                when: 'labels is not a list of non-empty strings of at most 64 characters.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000003', labels: ['VIP'] } }],
        public: true,
    },
    {
        name: 'os.mail_set_handled',
        family: 'os.mail_triage',
        summary: 'Record whether a mail in the assigned member’s inbox has been handled.',
        description:
            'Record where one message in the assigned member’s inbox stands. Pass id from os.mail_list and handledState: unhandled, replied, skipped, snoozed or escalated. A mail is in one state at a time. snoozed needs snoozedUntil, the date and time it comes back (ISO 8601); every other state takes no date and clears any snooze. os.mail_reply moves the mail it answers to replied on its own.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.mail_list' },
                handledState: { type: 'string', enum: HANDLED_STATES },
                snoozedUntil: {
                    type: 'string',
                    description: 'ISO 8601 date and time the mail comes back. Only with handledState snoozed',
                },
            },
            required: ['id', 'handledState'],
            additionalProperties: false,
        },
        returns: '`{ id, handledState, snoozedUntil }`: the state the mail is now in.',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        needs: { member: MAILBOX },
        errors: [
            ...NO_MAILBOX,
            NOT_IN_MAILBOX,
            {
                code: 'handled_state_invalid',
                when: 'handledState is not one of the handled states.',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_required',
                when: 'handledState is snoozed and snoozedUntil is missing or not a date.',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_not_allowed',
                when: 'snoozedUntil was given with a handledState other than snoozed.',
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000003',
                    handledState: 'snoozed',
                    snoozedUntil: '2026-10-01T09:00:00Z',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.mail_set_many',
        family: 'os.mail_triage',
        summary: 'Set the labels, the handled state, or both on many mails in the assigned member’s inbox at once.',
        description:
            'Record the same triage decision on many messages in the assigned member’s inbox in one call. Name the mail one way: ids (up to 200 message ids from os.mail_list), or fromAddress (every unhandled mail that arrived from that exact sender address; mail already handled and mail the member sent are never touched). Then pass labels (the whole set each mail should carry, replacing what it had), handledState (unhandled, replied, skipped, snoozed or escalated; snoozed needs snoozedUntil), or both. Use it to close a run of look-alike mail without reading each one. An id that is not in this mailbox changes nothing and comes back in notFound.',
        inputSchema: {
            type: 'object',
            properties: {
                ids: {
                    type: 'array',
                    items: { type: 'string' },
                    minItems: 1,
                    description: 'Up to 200 message ids from os.mail_list. Not together with fromAddress',
                },
                fromAddress: {
                    type: 'string',
                    description:
                        'A sender address, matched exactly: every unhandled mail that arrived from it. Not together with ids',
                },
                labels: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Every label each mail should carry. Replaces the current set',
                },
                handledState: { type: 'string', enum: HANDLED_STATES },
                snoozedUntil: {
                    type: 'string',
                    description: 'ISO 8601 date and time the mail comes back. Only with handledState snoozed',
                },
            },
            additionalProperties: false,
        },
        returns:
            '`{ updated, notFound }`: how many mails were changed, and the ids given that are not in this mailbox (always `[]` when selecting by `fromAddress`).',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        needs: { member: MAILBOX },
        errors: [
            ...NO_MAILBOX,
            {
                code: 'mail_selector_invalid',
                when: 'Neither ids nor fromAddress was given, or both were.',
                handling: 'tool_error',
            },
            {
                code: 'mail_ids_invalid',
                when: 'ids is not a list of 1 to 200 non-empty message ids.',
                handling: 'tool_error',
            },
            {
                code: 'mail_change_required',
                when: 'Neither labels nor handledState was given, so there is nothing to record.',
                handling: 'tool_error',
            },
            {
                code: 'labels_invalid',
                when: 'labels is not a list of non-empty strings of at most 64 characters.',
                handling: 'tool_error',
            },
            {
                code: 'handled_state_invalid',
                when: 'handledState is not one of the handled states.',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_required',
                when: 'handledState is snoozed and snoozedUntil is missing or not a date.',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_not_allowed',
                when: 'snoozedUntil was given with a handledState other than snoozed.',
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    fromAddress: 'updates@example.com',
                    labels: ['Newsletter'],
                    handledState: 'skipped',
                },
            },
            {
                input: {
                    ids: ['5f0c1d2e-0000-4000-8000-000000000003', '5f0c1d2e-0000-4000-8000-000000000004'],
                    handledState: 'skipped',
                },
            },
        ],
        public: true,
    },
]
